// MapLibre colour expressions, built from the same `stops` the legend and the
// charts read — so the map, the legend and a chart bar cannot disagree.
//
// This module is the MapLibre-coupled half. The plain-JS ramp evaluation
// (colorAt, gradientCss, bandScale, …) lives in src/lib/colors.ts so the city
// page and the pipeline-side code can use it without pulling in maplibre-gl; it
// is re-exported here so call sites keep one import for "the variables and their
// colours".

import { VARIABLES } from '../lib/variables.ts';
import { PALETTES, bandScale } from '../lib/colors.ts';
import type { VarKey } from '../lib/variables.ts';
import type { ExpressionSpecification } from 'maplibre-gl';

export { VARIABLES, PALETTES };
export { colorAt, domain, bandScale, gradientCss, clippedGradientCss } from '../lib/colors.ts';

// MapLibre color expression: interpolate the stops on a feature property.
export function colorExpression(varKey: VarKey): ExpressionSpecification {
  return colorExpressionOn(varKey, varKey);
}

// Same ramp, but reading an arbitrary property. Field tiles carry the band
// midpoint on a single `v` property (regardless of which variable it is), so the
// smooth-field layer colors with colorExpressionOn(varKey, 'v') and stays locked
// to the legend gradient.
//
// A `banded` variable is coloured in flat steps — one colour per named band —
// rather than a smooth blend, so the map reads as categories. Everything else
// keeps the continuous interpolate ramp.
export function colorExpressionOn(varKey: VarKey, prop: string): ExpressionSpecification {
  const meta = PALETTES[varKey];
  if (meta.banded && meta.bands) return stepExpressionOn(varKey, prop);
  // Only variables that are drawn carry stops, and only those reach here.
  const stops = meta.stops!;
  // MapLibre's ExpressionSpecification is a union of fixed-arity tuples, so an
  // expression grown a pair at a time cannot be inferred into it: build it as a
  // plain array and assert at the return.
  const expr: unknown[] = ['interpolate', ['linear'], ['get', prop]];
  for (const [v, c] of stops) expr.push(v, c);
  return expr as ExpressionSpecification;
}

// MapLibre `step` expression for a banded variable: a flat colour per band,
// switching at each band's upper edge. The per-band colour is exactly the swatch
// bandScale() gives the legend, so the flat field and the word key can't disagree.
// The final band runs to Infinity and needs no edge.
export function stepExpressionOn(varKey: VarKey, prop: string): ExpressionSpecification {
  // Only a banded variable has a step expression, and it has bands by definition.
  const bands = PALETTES[varKey].bands!;
  const scale = bandScale(varKey);
  const expr: unknown[] = ['step', ['get', prop], scale[0].color];
  for (let i = 0; i < bands.length - 1; i++) expr.push(bands[i][0], scale[i + 1].color);
  return expr as ExpressionSpecification;
}
